import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Key, Lock, Globe, CheckCircle } from 'lucide-react';

/**
 * WooCommerce Integration component for the chatbot builder
 * This component handles all WooCommerce-specific settings
 */
function WooCommerceIntegration({
  wooCommerceApiKey,
  setWooCommerceApiKey,
  wooCommerceApiSecret,
  setWooCommerceApiSecret,
  wooCommerceStoreUrl,
  setWooCommerceStoreUrl,
  wooCommerceEnabled,
  setWooCommerceEnabled,
  orderTrackingEnabled,
  setOrderTrackingEnabled,
  persistAndUpdate
}) {
  // Handle WooCommerce API Key change
  const handleWooCommerceApiKeyChange = useCallback(
    (e) => {
      const value = e.target.value;
      setWooCommerceApiKey(value);
      persistAndUpdate({
        wooCommerceApiKey: value,
        wooCommerceApiSecret: wooCommerceApiSecret,
        wooCommerceStoreUrl: wooCommerceStoreUrl,
        wooCommerceEnabled: wooCommerceEnabled,
        orderTrackingEnabled: wooCommerceEnabled
      });
    },
    [setWooCommerceApiKey, persistAndUpdate, wooCommerceApiSecret, wooCommerceStoreUrl, wooCommerceEnabled]
  );

  // Handle WooCommerce API Secret change
  const handleWooCommerceApiSecretChange = useCallback(
    (e) => {
      const value = e.target.value;
      setWooCommerceApiSecret(value);
      persistAndUpdate({
        wooCommerceApiKey: wooCommerceApiKey,
        wooCommerceApiSecret: value,
        wooCommerceStoreUrl: wooCommerceStoreUrl,
        wooCommerceEnabled: wooCommerceEnabled,
        orderTrackingEnabled: wooCommerceEnabled
      });
    },
    [setWooCommerceApiSecret, persistAndUpdate, wooCommerceApiKey, wooCommerceStoreUrl, wooCommerceEnabled]
  );

  // Handle WooCommerce Store URL change
  const handleWooCommerceStoreUrlChange = useCallback(
    (e) => {
      const value = e.target.value;
      setWooCommerceStoreUrl(value);
      persistAndUpdate({
        wooCommerceApiKey: wooCommerceApiKey,
        wooCommerceApiSecret: wooCommerceApiSecret,
        wooCommerceStoreUrl: value,
        wooCommerceEnabled: wooCommerceEnabled,
        orderTrackingEnabled: wooCommerceEnabled
      });
    },
    [setWooCommerceStoreUrl, persistAndUpdate, wooCommerceApiKey, wooCommerceApiSecret, wooCommerceEnabled]
  );

  // Handle WooCommerce Enabled toggle
  const handleWooCommerceEnabledChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setWooCommerceEnabled(checked);
      setOrderTrackingEnabled(checked);

      if (!checked) {
        setWooCommerceApiKey('');
        setWooCommerceApiSecret('');
        setWooCommerceStoreUrl('');
        persistAndUpdate({
          wooCommerceEnabled: false,
          orderTrackingEnabled: false,
          wooCommerceApiKey: '',
          wooCommerceApiSecret: '',
          wooCommerceStoreUrl: ''
        });
      } else {
        persistAndUpdate({
          wooCommerceEnabled: true,
          orderTrackingEnabled: true,
          wooCommerceApiKey: wooCommerceApiKey || '',
          wooCommerceApiSecret: wooCommerceApiSecret || '',
          wooCommerceStoreUrl: wooCommerceStoreUrl || ''
        });
      }
    },
    [setWooCommerceEnabled, setOrderTrackingEnabled, setWooCommerceApiKey, setWooCommerceApiSecret, setWooCommerceStoreUrl, persistAndUpdate, wooCommerceApiKey, wooCommerceApiSecret, wooCommerceStoreUrl]
  );

  return (
    <div className="space-y-6">


      {/* Main Enable/Disable Card */}
      <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold text-slate-800 text-lg">Enable Integration</h4>
            <div className="text-sm text-slate-500">Connect your WooCommerce store</div>
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={wooCommerceEnabled}
                onChange={handleWooCommerceEnabledChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-800"></div>
              <span className="ml-3 text-sm font-medium text-slate-700">
                {wooCommerceEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>
        <p className="text-sm text-slate-500 max-w-lg mt-2 leading-relaxed">
          Enable product synchronization and order tracking capabilities directly within your chatbot.
        </p>
      </div>

      {/* Configuration Form */}
      {wooCommerceEnabled && (
        <div className="space-y-5 animate-in fade-in slide-in-from-top-4 duration-300">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 ml-1">
              Store URL <span className="text-red-500">*</span>
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe className="h-4 w-4 text-slate-400 group-focus-within:text-slate-500 transition-colors" />
              </div>
              <input
                type="text"
                value={wooCommerceStoreUrl}
                onChange={handleWooCommerceStoreUrlChange}
                placeholder="https://yourstore.com"
                className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 sm:text-sm transition-all shadow-sm"
              />
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400 ml-1">
              The full URL to your WooCommerce store home page.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 ml-1">
                Consumer Key <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-4 w-4 text-slate-400 group-focus-within:text-slate-500 transition-colors" />
                </div>
                <input
                  type="text"
                  value={wooCommerceApiKey}
                  onChange={handleWooCommerceApiKeyChange}
                  placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxx"
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 sm:text-sm transition-all shadow-sm font-mono text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 ml-1">
                Consumer Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-slate-400 group-focus-within:text-slate-500 transition-colors" />
                </div>
                <input
                  type="password"
                  value={wooCommerceApiSecret}
                  onChange={handleWooCommerceApiSecretChange}
                  placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxx"
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 sm:text-sm transition-all shadow-sm font-mono text-xs"
                />
              </div>
            </div>
          </div>

          {/* Setup Guide */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-6">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-slate-600" />
              Setup Instructions
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-xs text-slate-600 ml-1">
              <li>Log in to your WordPress admin dashboard</li>
              <li>Navigate to <span className="font-semibold text-slate-700">WooCommerce &gt; Settings &gt; Advanced &gt; REST API</span></li>
              <li>Click <span className="font-semibold text-slate-700">"Add key"</span> and set permissions to <span className="font-semibold text-slate-700">Read/Write</span></li>
              <li>Copy the Consumer Key and Secret into the fields above</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

WooCommerceIntegration.propTypes = {
  wooCommerceApiKey: PropTypes.string.isRequired,
  setWooCommerceApiKey: PropTypes.func.isRequired,
  wooCommerceApiSecret: PropTypes.string.isRequired,
  setWooCommerceApiSecret: PropTypes.func.isRequired,
  wooCommerceStoreUrl: PropTypes.string.isRequired,
  setWooCommerceStoreUrl: PropTypes.func.isRequired,
  wooCommerceEnabled: PropTypes.bool.isRequired,
  setWooCommerceEnabled: PropTypes.func.isRequired,
  orderTrackingEnabled: PropTypes.bool.isRequired,
  setOrderTrackingEnabled: PropTypes.func.isRequired,
  persistAndUpdate: PropTypes.func.isRequired
};

export default WooCommerceIntegration;
